/* WOWNNY landing — Shopify build.
 * Every lookup is guarded: this page renders inside the Atelier theme, so the
 * original standalone header/footer are gone and their nodes are absent. An
 * unguarded `header.classList` here would throw and take the whole file with it.
 */
const header = document.querySelector("#site-header");
const scrollButtons = document.querySelectorAll("[data-scroll]");
const revealItems = document.querySelectorAll(".reveal");
const runButton = document.querySelector("#run-button");
const demo = document.querySelector("#conversation-demo");
const flowSteps = Array.from(document.querySelectorAll(".flow-track li"));
const sendPanel = document.querySelector("#send-panel");
const sendButtons = Array.from(document.querySelectorAll(".send-targets button"));
const sendStatus = document.querySelector("#send-status");
const sendConfirmation = document.querySelector("#send-confirmation");
const sendRoute = document.querySelector("#send-route");
const confirmOpen = document.querySelector("#confirm-open");
const agentRoute = document.querySelector("#agent-route");
const highlightButton = document.querySelector("#highlight-button");
const highlightToast = document.querySelector("#highlight-toast");
const liveConversation = document.querySelector(".live-conversation");
const pageVideos = Array.from(document.querySelectorAll("#wownny-root video"));

/**
 * Atelier does not scroll the window: `html` and `body` are both
 * `overflow: hidden` and the real scroller is `div.page-wrapper`. Find it by
 * behaviour rather than by class name, so this keeps working if the theme
 * renames things.
 */
function pageScroller() {
  const root = document.scrollingElement;
  if (
    root &&
    ["auto", "scroll", "overlay"].includes(getComputedStyle(root).overflowY) &&
    root.scrollHeight > root.clientHeight + 10
  ) {
    return root;
  }
  for (const el of document.querySelectorAll("body *")) {
    if (
      el.scrollHeight > el.clientHeight + 50 &&
      ["auto", "scroll", "overlay"].includes(getComputedStyle(el).overflowY)
    ) {
      return el;
    }
  }
  return document.scrollingElement;
}

if (header) {
  // window.scrollY is always 0 here, so the original listener never fired and
  // the header never picked up its scrolled state.
  const scroller = pageScroller();
  const syncScrolled = () => {
    header.classList.toggle("scrolled", (scroller?.scrollTop ?? 0) > 36);
  };
  scroller?.addEventListener("scroll", syncScrolled, { passive: true });
  window.addEventListener("scroll", syncScrolled, { passive: true });
  syncScrolled();
}

/* ---------- anchor scrolling under the theme's sticky header ----------
 * Two things about Atelier matter here.
 *
 * 1. It does not scroll the window. `html` and `body` are both `overflow:
 *    hidden` and the real scroller is `div.page-wrapper`, so `window.scrollTo`
 *    and `window.scrollY` are inert — anything built on them silently does
 *    nothing. `scrollIntoView` is fine: it walks up to whichever ancestor
 *    actually scrolls.
 * 2. Its header is sticky, so `block: "start"` parks the target underneath it.
 *
 * The fix is `scroll-margin-top`, which the browser applies during
 * `scrollIntoView`. The value is measured rather than hardcoded, because the
 * announcement bar and header height are both editable in the admin.
 */
const wyRoot = document.querySelector("#wownny-root");

// Our own header counts too. It used to be excluded because the theme's header
// was the only sticky chrome on the page; now the page ships its own fixed
// header and Atelier's group is hidden, so this is what anchors sit under.
function stickyChromeHeight() {
  let tallest = 0;
  for (const el of document.querySelectorAll("body header, body [class*='announcement']")) {
    if (!el.offsetHeight) continue;
    const style = getComputedStyle(el);
    if (style.position !== "fixed" && style.position !== "sticky") continue;
    // Only chrome pinned to the top edge displaces our anchors. Use the
    // element's own height, not its current position: at the top of the page
    // the header still sits below the announcement bar, but once the page
    // scrolls it is the header alone that stays pinned.
    const top = parseFloat(style.top);
    if (!Number.isFinite(top) || top > 8) continue;
    tallest = Math.max(tallest, el.offsetHeight);
  }
  return Math.round(tallest);
}

function syncStickyOffset() {
  wyRoot?.style.setProperty("--wy-sticky-top", `${stickyChromeHeight()}px`);
}

syncStickyOffset();
window.addEventListener("resize", syncStickyOffset, { passive: true });
window.addEventListener("load", syncStickyOffset);

/**
 * Scroll to an anchor and keep correcting until it actually parks there.
 *
 * A plain scrollIntoView is not enough on this page. Several sections reserve
 * far more height than they need until their <video> reports real dimensions —
 * measured live, `handoff-band` alone collapses from 956px to 250px as it
 * loads, and roughly 836px disappears from above the buy block. The browser
 * locks its scroll destination in at click time, so when that collapse happens
 * mid-animation the visitor lands ~760px past the section they asked for, with
 * no indication anything went wrong.
 *
 * Rather than reserve space for every video (the sections above are untouched
 * original markup), watch the target until it stops moving and re-issue the
 * scroll if it did not end up under the theme's sticky header.
 */
function scrollToAnchor(target) {
  syncStickyOffset();
  target.scrollIntoView({ behavior: "smooth", block: "start" });

  const deadline = performance.now() + 3000;
  let corrections = 0;
  let lastTop = null;
  let steady = 0;

  const settle = () => {
    const top = Math.round(target.getBoundingClientRect().top);
    const wanted = Math.round(parseFloat(getComputedStyle(target).scrollMarginTop) || 0);

    steady = top === lastTop ? steady + 1 : 0;
    lastTop = top;

    if (steady >= 4) {
      // Parked, or as close as the page allows (a section near the bottom
      // cannot always reach the top — hence the correction cap).
      if (Math.abs(top - wanted) <= 4 || corrections >= 4) return;
      corrections += 1;
      target.scrollIntoView({ behavior: "auto", block: "start" });
      steady = 0;
      lastTop = null;
    }

    if (performance.now() < deadline) requestAnimationFrame(settle);
  };

  requestAnimationFrame(settle);
}

scrollButtons.forEach((button) => {
  button.addEventListener("click", (event) => {
    const target = document.querySelector(button.dataset.scroll);
    if (!target) return; // let a real href do whatever it would have done
    // The nav items are <a href="#…">. Without this the browser's own jump
    // races the corrected scroll and wins, landing under the header again.
    event.preventDefault();
    scrollToAnchor(target);
  });
});

const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("visible");
        revealObserver.unobserve(entry.target);
      }
    });
  },
  { threshold: 0.12 },
);

revealItems.forEach((item) => revealObserver.observe(item));

/* ---------- memory demo ---------- */

let demoTimers = [];

function clearDemoTimers() {
  demoTimers.forEach((timer) => window.clearTimeout(timer));
  demoTimers = [];
}

function activateStep(index) {
  flowSteps.forEach((step, stepIndex) => {
    step.classList.toggle("active", stepIndex === index);
  });
  sendPanel?.classList.toggle("active", index === 3);
}

if (runButton && demo) {
  runButton.addEventListener("click", () => {
    clearDemoTimers();
    demo.classList.remove("is-running", "is-complete");
    sendPanel?.classList.remove("active");
    activateStep(0);
    void demo.offsetWidth;
    demo.classList.add("is-running");
    runButton.lastChild.textContent = " Replay the memory";
    if (sendStatus) {
      sendStatus.textContent =
        "Your memory is ready. Choose where it should help you next.";
    }

    flowSteps.forEach((_, index) => {
      const timer = window.setTimeout(() => activateStep(index), index * 760);
      demoTimers.push(timer);
    });

    const completeTimer = window.setTimeout(() => {
      demo.classList.remove("is-running");
      demo.classList.add("is-complete");
      sendPanel?.classList.remove("active");
    }, 4200);
    demoTimers.push(completeTimer);
  });
}

sendButtons.forEach((button) => {
  button.setAttribute("aria-pressed", "false");
  button.addEventListener("click", () => {
    sendButtons.forEach((item) => {
      const selected = item === button;
      item.classList.toggle("selected", selected);
      item.setAttribute("aria-pressed", String(selected));
    });
    sendPanel?.classList.add("active");
    sendConfirmation?.classList.add("ready");
    sendConfirmation?.classList.remove("confirmed");
    if (sendRoute) {
      sendRoute.textContent = `Creative direction / Today → ${button.dataset.target}`;
    }
    if (confirmOpen) {
      confirmOpen.disabled = false;
      confirmOpen.dataset.target = button.dataset.target;
    }
    if (sendStatus) {
      sendStatus.textContent = `02 / Review this memory in ${button.dataset.target}. Nothing has been shared yet.`;
    }
  });
});

confirmOpen?.addEventListener("click", () => {
  if (confirmOpen.disabled) return;
  sendConfirmation?.classList.add("confirmed");
  if (sendRoute) sendRoute.textContent = `Confirmed → ${confirmOpen.dataset.target}`;
  if (sendStatus) {
    sendStatus.textContent = `03 / Approved. This memory would open in ${confirmOpen.dataset.target}. Preview only.`;
  }
});

agentRoute?.addEventListener("click", () => {
  sendButtons.forEach((button) => {
    button.classList.remove("selected");
    button.setAttribute("aria-pressed", "false");
  });
  sendPanel?.classList.add("active");
  sendConfirmation?.classList.add("ready");
  sendConfirmation?.classList.remove("confirmed");
  if (sendRoute) sendRoute.textContent = "WOWNNY Memory → Find the best destination";
  if (confirmOpen) {
    confirmOpen.disabled = false;
    confirmOpen.dataset.target = "WOWNNY Agent";
  }
  if (sendStatus) {
    sendStatus.textContent =
      "02 / Memory assistant selected. Review its suggestion, then approve it.";
  }
});

highlightButton?.addEventListener("click", () => {
  liveConversation?.classList.add("highlighted");
  highlightButton.classList.add("is-highlighted");
  highlightButton.setAttribute("aria-pressed", "true");
  highlightButton.lastChild.textContent = " Remembered";
  if (highlightToast) highlightToast.textContent = "MEMORY KEPT / 00:24";
});

/* ---------- pre-order ----------
 * The form is a real POST to /cart/add, so it still works with JS disabled
 * (Shopify adds the line item and redirects to /cart). When JS is available we
 * intercept it, use /cart/add.js, and go straight to checkout.
 */

/**
 * Do NOT stopPropagation() on the submit events of these forms.
 *
 * A submit here leaves this form with `defaultPrevented === false` and arrives
 * at `document` with it `true`, which looks exactly like a theme handler eating
 * our form. It is not: this store has Shopify's spam protection switched on,
 * and that script is the ancestor listener. It cancels the submit, fetches a
 * CAPTCHA token, and re-submits. Cut it off and Shopify answers the POST with
 * `400 Missing CAPTCHA token` — which is what happened when this file briefly
 * carried a stopPropagation "fix".
 *
 * In a headless browser the token step often never finishes, so the submit
 * appears to do nothing at all. That is the harness, not the page.
 */

const buyForm = document.querySelector("#buy-form");

if (buyForm) {
  const buyError = document.querySelector("#buy-error");
  const buySubmit = buyForm.querySelector(".buy-submit");
  const colourName = document.querySelector("#buy-colour-name");
  const buyShots = Array.from(document.querySelectorAll("[data-buy-shot]"));
  const radios = Array.from(buyForm.querySelectorAll('input[name="id"]'));

  const syncSwatch = (input) => {
    if (colourName) colourName.textContent = input.dataset.colour || "";
    buyShots.forEach((shot) => {
      shot.classList.toggle("is-active", shot.dataset.buyShot === input.value);
    });
  };

  const checked = radios.find((radio) => radio.checked);
  if (checked) syncSwatch(checked);
  radios.forEach((radio) => {
    radio.addEventListener("change", () => {
      if (radio.checked) syncSwatch(radio);
    });
  });

  const showError = (message) => {
    if (!buyError) return false;
    buyError.textContent = message;
    buyError.hidden = false;
    return true;
  };

  buyForm.addEventListener("submit", async (event) => {
    const variantId = new FormData(buyForm).get("id");
    if (!variantId) return; // nothing to add — let native validation speak
    event.preventDefault();

    if (buySubmit?.classList.contains("is-busy")) return;
    buySubmit?.classList.add("is-busy");
    if (buyError) {
      buyError.hidden = true;
      buyError.textContent = "";
    }

    let response;
    try {
      response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
    } catch {
      // Network or CORS failure — fall back to the plain form POST.
      buyForm.submit();
      return;
    }

    if (response.ok) {
      window.location.href = "/checkout";
      return;
    }

    // Shopify answered but refused (sold out, limit reached). Say why.
    const data = await response.json().catch(() => ({}));
    const reason =
      data.description ||
      data.message ||
      "We couldn't add that to your cart right now.";
    buySubmit?.classList.remove("is-busy");
    if (!showError(reason)) buyForm.submit();
  });
}

/* ---------- video autoplay ---------- */

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

if (reducedMotion.matches) {
  pageVideos.forEach((video) => video.pause());
} else {
  const videoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        video.dataset.inView = String(entry.isIntersecting);
        if (entry.isIntersecting && !document.hidden) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    },
    { rootMargin: "180px 0px", threshold: 0.12 },
  );

  pageVideos.forEach((video) => videoObserver.observe(video));

  document.addEventListener("visibilitychange", () => {
    pageVideos.forEach((video) => {
      if (document.hidden) {
        video.pause();
      } else if (video.dataset.inView === "true") {
        video.play().catch(() => {});
      }
    });
  });
}

window.__WY_BUILD__ = "9b934262df";
